import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconPlay, IconPause, IconCheck, IconArrowRight, IconVolume, IconMute, IconFullscreen } from './Icons';

/**
 * Video player.
 *
 * When `src` is set (a Firebase Storage URL), a real <video> element is
 * rendered and progress is tracked natively: once WATCH_THRESHOLD of the clip
 * is watched, the next step unlocks and `onWatched` fires exactly once.
 * When no `src` is given, falls back to a simulated player so lessons without a
 * video still satisfy the "duration is tracked" / completion flow.
 *
 * The live watched percentage is kept in local state only (for the progress
 * label / gate). We persist just a single boolean once the threshold is
 * crossed — no per-second DB writes.
 */
const WATCH_THRESHOLD = 0.7;

const fmt = (sec) => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const VideoPlayer = ({
  src,
  poster,
  posterLabel,
  durationSeconds = 60,
  initiallyWatched = false,
  onWatched,
  nextStep,
  onNext,
}) => {
  if (src) {
    return (
      <RealVideoPlayer
        src={src}
        poster={poster}
        initiallyWatched={initiallyWatched}
        onWatched={onWatched}
        nextStep={nextStep}
        onNext={onNext}
      />
    );
  }
  return (
    <SimulatedVideoPlayer
      poster={poster}
      posterLabel={posterLabel}
      durationSeconds={durationSeconds}
      initiallyWatched={initiallyWatched}
      onWatched={onWatched}
      nextStep={nextStep}
      onNext={onNext}
    />
  );
};

/* Real HTML5 video with fully custom, branded controls + watch tracking.
 * Native controls are intentionally disabled so the player matches the
 * Academy look (play/pause, seek bar, time, mute, fullscreen). */
const RealVideoPlayer = ({ src, poster, initiallyWatched, onWatched, nextStep, onNext }) => {
  const [watched, setWatched] = useState(initiallyWatched);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Highest fraction (0–1) of the clip the user has reached so far. Seeking
  // back doesn't lower it; only forward progress counts toward the threshold.
  const [maxFraction, setMaxFraction] = useState(initiallyWatched ? 1 : 0);
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  // Guards against firing onWatched (→ a DB write) more than once.
  const firedRef = useRef(initiallyWatched);

  const markWatched = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    setWatched(true);
    if (onWatched) onWatched();
  }, [onWatched]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else if (el.requestFullscreen) el.requestFullscreen();
  }, []);

  const handleTimeUpdate = (e) => {
    const v = e.currentTarget;
    setCurrentTime(v.currentTime);
    if (!v.duration) return;
    const fraction = v.currentTime / v.duration;
    setMaxFraction((prev) => (fraction > prev ? fraction : prev));
    if (fraction >= WATCH_THRESHOLD) markWatched();
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    v.currentTime = ratio * v.duration;
    setCurrentTime(v.currentTime);
  };

  const playedPct = duration ? Math.min(100, (currentTime / duration) * 100) : 0;

  return (
    <div className={`ac-video is-real ${playing ? 'is-playing' : ''}`} ref={wrapRef}>
      <video
        ref={videoRef}
        src={src}
        poster={poster || undefined}
        playsInline
        preload="metadata"
        className="ac-video-el"
        onClick={togglePlay}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); markWatched(); }}
      />

      {/* Center play button — only while paused. */}
      {!playing && (
        <button type="button" className="ac-video-bigplay" onClick={togglePlay} aria-label="Play">
          <IconPlay size={30} />
        </button>
      )}

      {watched && (
        <span className="ac-video-watched-tag">
          <span className="ac-badge is-complete">
            <IconCheck /> Watched
          </span>
        </span>
      )}

      {/* Next step — only appears once the lesson is unlockable (>= threshold). */}
      {nextStep && watched && onNext && (
        <button type="button" className="ac-video-next is-ready" onClick={onNext}>
          <span className="ac-video-next-text">
            <span className="ac-video-next-kicker">{nextStep.kicker}</span>
            <span className="ac-video-next-label">{nextStep.label}</span>
          </span>
          <IconArrowRight size={15} />
        </button>
      )}

      {/* Custom branded control bar. */}
      <div className="ac-video-controls" onClick={(e) => e.stopPropagation()}>
        <div className="ac-video-bar" onClick={handleSeek}>
          <div className="ac-video-bar-watched" style={{ width: `${Math.min(100, maxFraction * 100)}%` }} />
          <div className="ac-video-bar-fill" style={{ width: `${playedPct}%` }}>
            <span className="ac-video-bar-knob" />
          </div>
        </div>
        <div className="ac-video-controls-row">
          <button type="button" className="ac-video-ctrl" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? <IconPause size={20} /> : <IconPlay size={20} />}
          </button>
          <button type="button" className="ac-video-ctrl" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
            {muted ? <IconMute size={18} /> : <IconVolume size={18} />}
          </button>
          <span className="ac-video-ctrl-time">{fmt(currentTime)} / {fmt(duration)}</span>
          <span className="ac-video-ctrl-spacer" />
          <button type="button" className="ac-video-ctrl" onClick={toggleFullscreen} aria-label="Fullscreen">
            <IconFullscreen size={17} />
          </button>
        </div>
      </div>
    </div>
  );
};

const SimulatedVideoPlayer = ({
  poster,
  posterLabel,
  durationSeconds = 60,
  initiallyWatched = false,
  onWatched,
  nextStep,
  onNext,
}) => {
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(initiallyWatched ? durationSeconds : 0);
  const [watched, setWatched] = useState(initiallyWatched);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!playing) return undefined;
    timerRef.current = setInterval(() => {
      setElapsed((prev) => {
        const next = prev + 1;
        if (next >= durationSeconds) {
          clearInterval(timerRef.current);
          setPlaying(false);
          return durationSeconds;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [playing, durationSeconds]);

  useEffect(() => {
    if (!watched && elapsed >= durationSeconds * WATCH_THRESHOLD) {
      setWatched(true);
      if (onWatched) onWatched();
    }
  }, [elapsed, durationSeconds, watched, onWatched]);

  const pct = Math.min(100, Math.round((elapsed / durationSeconds) * 100));

  const togglePlay = () => setPlaying((p) => !p);

  return (
    <div className={`ac-video ${playing ? 'is-playing' : ''}`} onClick={togglePlay}>
      <div className="ac-video-poster">
        {poster ? (
          <img src={poster} alt={posterLabel || 'Video preview'} className="ac-video-poster-img" />
        ) : (
          <span className="ac-video-poster-label">{posterLabel}</span>
        )}
        <span className="ac-video-poster-shade" />
      </div>

      {watched && (
        <span className="ac-video-watched-tag">
          <span className="ac-badge is-complete">
            <IconCheck /> Watched
          </span>
        </span>
      )}

      <span className="ac-video-play">
        <IconPlay />
      </span>

      <span className="ac-video-time">
        {fmt(elapsed)} / {fmt(durationSeconds)}
      </span>

      {nextStep && watched && (
        <button
          type="button"
          className="ac-video-next is-ready"
          onClick={(e) => {
            e.stopPropagation();
            if (onNext) onNext();
          }}
        >
          <span className="ac-video-next-text">
            <span className="ac-video-next-kicker">{nextStep.kicker}</span>
            <span className="ac-video-next-label">{nextStep.label}</span>
          </span>
          <IconArrowRight size={15} />
        </button>
      )}

      <div className="ac-video-scrubber">
        <div className="ac-video-scrubber-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default VideoPlayer;
